<template>
  <div>
    <nav
      class="navbar navbar-expand-lg justify-content-between justify-content-md-around sticky-top"
    >
      <img
        alt="logo"
        src="./assets/imgs/nextgenlogo.png"
        class="cursor--pointer navbar-brand ml-4"
        @click="reloadPage"
      />

      <!-- Phone number moved outside navbar-collapse -->
      <div class="phone-number d-lg-none">
        <a href="tel:5514080587" class="phone-text text-primaryblue mr-4">
          <svg
            aria-hidden="true"
            class="phone-icon"
            viewBox="0 0 512 512"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            width="16px"
            height="16px"
          >
            <path
              d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"
            ></path>
          </svg>
          (551) 408-0587
        </a>
      </div>

      <button
        class="navbar-toggler"
        type="button"
        data-toggle="collapse"
        data-target="#navbarTogglerDemo02"
        aria-controls="navbarTogglerDemo02"
        aria-expanded="false"
        aria-label="Toggle navigation"
        @click="toggleMobileMenu"
        :class="{ active: mobileMenuOpen }"
      >
        <div class="hamburger-lines">
          <span class="line line1"></span>
          <span class="line line2"></span>
          <span class="line line3"></span>
        </div>
      </button>

      <div
        class="collapse navbar-collapse flex-grow-0"
        id="navbarTogglerDemo02"
        :class="{
          show: mobileMenuOpen,
          closing: isClosing,
        }"
      >
        <ul class="navbar-nav ml-auto mt-2 mt-lg-0 align-items-center">
          <li class="nav-item" :class="{ active: $route.path === '/' }">
            <router-link to="/" class="nav-link" @click="closeMobileMenu">
              Home
              <span class="sr-only" v-if="$route.path === '/'">(current)</span>
            </router-link>
          </li>
          <li
            class="nav-item dropdown"
            :class="{ active: $route.path.startsWith('/services') }"
            @mouseover="openServicesDropdown"
            @mouseleave="closeServicesDropdown"
          >
            <div class="nav-link-wrapper">
              <router-link
                to="/services"
                class="nav-link dropdown-toggle"
                @click.prevent="handleServicesClick"
                @touchstart.prevent="handleServicesClick"
                :class="{ show: servicesDropdownOpen }"
              >
                Services
                <span class="sr-only" v-if="$route.path.startsWith('/services')"
                  >(current)</span
                >
              </router-link>
              <span
                class="toggle-icon"
                @click.stop="handleServicesClick"
                @touchstart.stop="handleServicesClick"
                :class="{ 'rotate-icon': servicesDropdownOpen }"
              >
                +
              </span>
            </div>
            <div class="dropdown-menu" :class="{ show: servicesDropdownOpen }">
              <router-link
                v-for="service in services"
                :key="service.slug"
                :to="'/services/' + service.slug"
                class="dropdown-item"
                :class="{ active: $route.path === '/services/' + service.slug }"
                @click="closeMobileMenu"
                @touchend="closeMobileMenu"
              >
                {{ service.name }}
              </router-link>
            </div>
          </li>
          <li
            class="nav-item dropdown"
            :class="{ active: $route.path.startsWith('/service-areas') }"
            @mouseover="openServiceAreasDropdown"
            @mouseleave="closeServiceAreasDropdown"
          >
            <div class="nav-link-wrapper">
              <router-link
                to="/service-areas"
                class="nav-link dropdown-toggle"
                @click.prevent="handleServiceAreasClick"
                @touchstart.prevent="handleServiceAreasClick"
                :class="{ show: serviceAreasDropdownOpen }"
              >
                Service Areas
                <span
                  class="sr-only"
                  v-if="$route.path.startsWith('/service-areas')"
                  >(current)</span
                >
              </router-link>
              <span
                class="toggle-icon"
                @click.stop="handleServiceAreasClick"
                @touchstart.stop="handleServiceAreasClick"
                :class="{ 'rotate-icon': serviceAreasDropdownOpen }"
              >
                +
              </span>
            </div>
            <div
              class="dropdown-menu"
              :class="{ show: serviceAreasDropdownOpen }"
            >
              <router-link
                v-for="area in serviceAreas"
                :key="area.name"
                :to="`/service-areas/${area.name
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`"
                class="dropdown-item"
                :class="{
                  active:
                    $route.path ===
                    `/service-areas/${area.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`,
                }"
                @click="closeMobileMenu"
                @touchend="closeMobileMenu"
              >
                {{ area.name }}
              </router-link>
            </div>
          </li>
          <li class="nav-item" :class="{ active: $route.path === '/about' }">
            <router-link to="/about" class="nav-link" @click="closeMobileMenu">
              About
            </router-link>
          </li>
          <li
            class="nav-item contact"
            :class="{ active: $route.path === '/contact' }"
          >
            <router-link
              to="/contact"
              class="nav-link"
              @click="closeMobileMenu"
            >
              Contact
            </router-link>
          </li>
          <!-- Phone number remains in navbar-collapse for desktop -->
          <li class="nav-item phone-number d-none d-lg-block">
            <a href="tel:5514080587" class="phone">
              <span class="nav-link phone-text">
                <svg
                  aria-hidden="true"
                  class="phone-icon"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  width="16px"
                  height="16px"
                >
                  <path
                    d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"
                  ></path>
                </svg>
                (551) 408-0587
              </span>
            </a>
          </li>
          <li class="nav-item">
            <a
              href="tel:5514080587"
              class="btn primaryblue--btn text-white quote-btn"
            >
              Get Free Quote
            </a>
          </li>
        </ul>
      </div>
    </nav>

    <div
      v-if="mobileMenuOpen"
      class="mobile-overlay"
      :class="{ show: mobileMenuOpen }"
      @click="closeMobileMenu"
    ></div>

    <div class="main">
      <router-view />
    </div>

    <!-- Floating Emergency Service Button -->
    <a
      href="tel:5514080587"
      class="emergency-btn"
      title="24/7 Emergency Service"
    >
      <svg
        aria-hidden="true"
        class="emergency-icon"
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        width="24px"
        height="24px"
      >
        <path
          d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"
        ></path>
      </svg>
      <span>24/7 Emergency Service</span>
    </a>

    <div
      class="footer p-3 d-flex justify-content-center align-items-center flex-column"
    >
      <img
        src="./assets/imgs/nextgenlogo.png"
        class="img-fluid cursor--pointer"
        style="max-width: 200px"
        alt="Logo"
        title="NextGen Garage Door Repairs"
        @click="handleLogoClick"
      />

      <div class="text-center mb-3">
        <p class="footer-text mb-0">
          <span class="font-weight-bold">Service Areas: </span>
          <span class="d-inline-block">
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/englewood"
              >Englewood</router-link
            >,
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/bergenfield"
              >Bergenfield</router-link
            >,
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/englewood-cliffs"
              >Englewood Cliffs</router-link
            >,
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/fort-lee"
              >Fort Lee</router-link
            >,
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/leonia"
              >Leonia</router-link
            >,
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/teaneck"
              >Teaneck</router-link
            >,
            <router-link
              @click.native="handleFooterNavClick"
              class="text-dark"
              to="/service-areas/tenafly"
              >Tenafly</router-link
            >.
          </span>
        </p>
      </div>

      <div class="container-fluid">
        <div class="row justify-content-center text-center text-md-left">
          <div class="mb-2 mb-md-0">
            <a
              href="tel:5514080587"
              class="mb-0 mx-3 footer-text d-flex align-items-center justify-content-center justify-content-md-start"
            >
              <svg
                aria-hidden="true"
                class="e-font-icon-svg e-fas-phone-alt mr-2 flex-shrink-0"
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
                fill="white"
                width="16px"
                height="16px"
              >
                <path
                  d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"
                ></path>
              </svg>
              (551) 408-0587
            </a>
          </div>
          <div>|</div>

          <div class="">
            <p
              class="mb-0 mx-3 footer-text d-flex align-items-center justify-content-center justify-content-md-start"
            >
              <svg
                aria-hidden="true"
                class="e-font-icon-svg e-fas-search-location mr-2 flex-shrink-0"
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
                fill="white"
                width="16px"
                height="16px"
              >
                <path
                  d="M505.04 442.66l-99.71-99.69c-4.5-4.5-10.6-7-17-7h-16.3c27.6-35.3 44-79.69 44-127.99C416.03 93.09 322.92 0 208.02 0S0 93.09 0 207.98s93.11 207.98 208.02 207.98c48.3 0 92.71-16.4 128.01-44v16.3c0 6.4 2.5 12.5 7 17l99.71 99.69c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.59.1-33.99zm-297.02-90.7c-79.54 0-144-64.34-144-143.98 0-79.53 64.35-143.98 144-143.98 79.54 0 144 64.34 144 143.98 0 79.53-64.35 143.98-144 143.98zm.02-239.96c-40.78 0-73.84 33.05-73.84 73.83 0 32.96 48.26 93.05 66.75 114.86a9.24 9.24 0 0 0 14.18 0c18.49-21.81 66.75-81.89 66.75-114.86 0-40.78-33.06-73.83-73.84-73.83zm0 96c-13.26 0-24-10.75-24-24 0-13.26 10.75-24 24-24s24 10.74 24 24c0 13.25-10.75 24-24 24z"
                ></path>
              </svg>
              <span class="d-block d-sm-inline"
                >25 Rockwood Pl, Englewood, NJ 07631
              </span>
            </p>
          </div>
        </div>
      </div>

      <div class="mt-3">
        <ul class="navbar-nav flex-row justify-content-center flex-wrap">
          <li
            class="nav-item mx-2 mx-md-3"
            :class="{ active: $route.path === '/' }"
          >
            <router-link
              to="/"
              class="nav-link text-dark px-2 py-1"
              @click.native="handleFooterNavClick"
              >Home</router-link
            >
          </li>
          <li
            class="nav-item mx-2 mx-md-3"
            :class="{ active: $route.path === '/about' }"
          >
            <router-link
              to="/about"
              class="nav-link text-dark px-2 py-1"
              @click.native="handleFooterNavClick"
              >About</router-link
            >
          </li>
          <li
            class="nav-item mx-2 mx-md-3"
            :class="{ active: $route.path.startsWith('/services') }"
          >
            <router-link
              to="/services"
              class="nav-link text-dark px-2 py-1"
              @click.native="handleFooterNavClick"
              >Services</router-link
            >
          </li>
          <li
            class="nav-item mx-2 mx-md-3"
            :class="{ active: $route.path === '/service-areas' }"
          >
            <router-link
              to="/service-areas"
              class="nav-link text-dark px-2 py-1"
              @click.native="handleFooterNavClick"
              >Service Areas</router-link
            >
          </li>
          <li
            class="nav-item mx-2 mx-md-3"
            :class="{ active: $route.path === '/contact' }"
          >
            <router-link
              to="/contact"
              class="nav-link text-dark px-2 py-1"
              @click.native="handleFooterNavClick"
              >Contact</router-link
            >
          </li>
        </ul>
      </div>

      <div class="mt-3 pt-3 border-top border-secondary w-100 text-center">
        <p class="mb-0 footer-text">© 2025 All Rights Are Reserved</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      privacyModalVisible: false,
      actionTermsModalVisible: false,
      mobileMenuOpen: false,
      isClosing: false,
      servicesDropdownOpen: false,
      serviceAreasDropdownOpen: false,
      serviceAreas: [
        { name: "Englewood" },
        { name: "Bergenfield" },
        { name: "Englewood Cliffs" },
        { name: "Fort Lee" },
        { name: "Leonia" },
        { name: "Teaneck" },
        { name: "Tenafly" },
      ],
      services: [
        {
          name: "New garage door installations ",
          slug: "new-garage-door-installations-residential-commercial",
        },
        {
          name: "Replacement of old or damaged doors",
          slug: "replacement-old-damaged-doors",
        },
        {
          name: "Broken spring replacement",
          slug: "broken-spring-replacement",
        },
        {
          name: "Cable repair & replacement",
          slug: "cable-repair-replacement",
        },
        { name: "Panel replacement", slug: "panel-replacement" },
        { name: "Roller and hinge repair", slug: "roller-and-hinge-repair" },
        {
          name: "Track alignment and repair",
          slug: "track-alignment-and-repair",
        },
        {
          name: "Opener repair and troubleshooting",
          slug: "opener-repair-and-troubleshooting",
        },
        {
          name: "Remote and keypad programming",
          slug: "remote-and-keypad-programming",
        },
        {
          name: "Fast response for stuck or non-functioning doors",
          slug: "emergency-stuck-doors",
        },
      ],
    };
  },
  watch: {
    $route() {
      this.closeMobileMenu();
      this.servicesDropdownOpen = false;
      this.serviceAreasDropdownOpen = false; // Add this line
    },
  },
  mounted() {
    this.$watch("mobileMenuOpen", (isOpen) => {
      if (isOpen) {
        document.body.classList.add("mobile-menu-open");
      } else {
        document.body.classList.remove("mobile-menu-open");
      }
    });
  },
  beforeDestroy() {
    document.body.classList.remove("mobile-menu-open");
  },
  methods: {
    scrollToServices() {
      this.closeMobileMenu();
      if (this.$route.path !== "/") {
        this.$router.push("/").then(() => {
          this.$nextTick(() => {
            const element = document.getElementById("services");
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          });
        });
      } else {
        const element = document.getElementById("services");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    },
    showText() {},
    showPrivacyModal() {
      this.privacyModalVisible = true;
    },
    showActionTermsModal() {
      this.actionTermsModalVisible = true;
    },
    reloadPage() {
      this.$router.push("/");
    },
    toggleMobileMenu() {
      if (this.mobileMenuOpen) {
        this.closeMobileMenu();
      } else {
        this.mobileMenuOpen = true;
      }
    },

    handleLogoClick() {
      if (this.$route.path === "/") {
        // If already on home page, scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // Navigate to home page and scroll to top
        this.$router.push("/").then(() => {
          this.$nextTick(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          });
        });
      }
    },

    handleFooterNavClick() {
      // Always scroll to top after navigation with proper timing
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    },

    // Update the existing closeMobileMenu method to ensure proper closing
    closeMobileMenu() {
      if (this.mobileMenuOpen) {
        this.isClosing = true;
        this.servicesDropdownOpen = false;
        this.serviceAreasDropdownOpen = false;
        setTimeout(() => {
          this.mobileMenuOpen = false;
          this.isClosing = false;
        }, 300);
      }
    },
    handleServiceAreasClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (window.innerWidth < 992) {
        this.serviceAreasDropdownOpen = !this.serviceAreasDropdownOpen;
      } else {
        this.$router.push("/service-areas");
      }
    },

    openServiceAreasDropdown() {
      if (window.innerWidth >= 992) {
        this.servicesDropdownOpen = false;
        this.serviceAreasDropdownOpen = true;
      }
    },
    closeServiceAreasDropdown() {
      if (window.innerWidth >= 992) {
        setTimeout(() => {
          if (!this.$el.querySelector(".dropdown:hover")) {
            this.serviceAreasDropdownOpen = false;
          }
        }, 200);
      }
    },
    toggleServicesDropdown(event) {
      if (window.innerWidth < 992) {
        this.servicesDropdownOpen = !this.servicesDropdownOpen;
      } else {
        this.$router.push("/services");
      }
    },
    handleServicesClick(event) {
      // Force prevent default for touch devices
      event.preventDefault();
      event.stopPropagation();

      // Toggle dropdown on mobile
      if (window.innerWidth < 992) {
        this.servicesDropdownOpen = !this.servicesDropdownOpen;
      } else {
        this.$router.push("/services");
      }
    },
    openServicesDropdown() {
      if (window.innerWidth >= 992) {
        this.servicesDropdownOpen = true;
        this.serviceAreasDropdownOpen = false;
      }
    },
    closeServicesDropdown() {
      if (window.innerWidth >= 992) {
        setTimeout(() => {
          if (!this.$el.querySelector(".dropdown:hover")) {
            this.servicesDropdownOpen = false;
          }
        }, 200);
      }
    },
  },
};
</script>

<style lang="scss">
.phone {
  text-decoration: none !important;
}
.navbar.sticky-top {
  position: sticky !important;
  top: 0 !important;
  z-index: 1050 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}
.navbar-nav {
  .nav-link {
    position: relative;
  }
  :hover.nav-link {
    color: #0d47a1 !important;
  }

  .nav-item.active .nav-link {
    color: #0d47a1 !important;
    font-weight: 700;

    &::after {
      content: "";
      position: absolute;
      bottom: -5px;
      left: 50%;
      transform: translateX(-50%);
      width: 20px;
      height: 3px;
      background-color: #0d47a1;
      border-radius: 2px;
    }
  }

  .dropdown {
    position: relative;

    .nav-link-wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .dropdown-toggle {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;

      &::after {
        display: inline-block;
        margin-left: 0.255em;
        vertical-align: 0.255em;
        content: "";
        border-top: 0.3em solid;
        border-right: 0.3em solid transparent;
        border-left: 0.3em solid transparent;
        transition: transform 0.3s ease;

        @media (max-width: 991.99px) {
          display: none;
        }
      }

      &.show::after {
        transform: rotate(180deg);
      }
    }

    .toggle-icon {
      display: none;
      font-size: 30px;
      font-weight: bold;
      color: #1d1729;
      cursor: pointer;
      transition: transform 0.3s ease, color 0.3s ease;

      @media (max-width: 991.99px) {
        display: inline-block;
        margin-right: 2rem;
      }

      &.rotate-icon {
        transform: rotate(45deg);
        color: #0d47a1;
      }
    }

    .dropdown-menu {
      display: none;
      position: absolute;
      top: 100%;
      left: 0;
      min-width: 280px;
      background: #ffffff;
      border: none;
      border-radius: 8px;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
      z-index: 1000;
      padding: 1rem 0;
      transition: opacity 0.3s ease, transform 0.3s ease;

      &.show {
        display: block;
        opacity: 1;
        transform: translateY(0);
        animation: slideDown 0.3s ease forwards;
      }

      .dropdown-item {
        color: #1d1729;
        padding: 0.75rem 1.5rem;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        transition: all 0.2s ease;
        position: relative;
        overflow: hidden;

        &::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 4px;
          background-color: #0d47a1;
          transform: translateX(-100%);
          transition: transform 0.2s ease;
        }

        &:hover {
          background-color: rgba(13, 71, 161, 0.1);
          color: #0d47a1;
          padding-left: 1.75rem;
          transform: scale(1.02);
          box-shadow: inset 0 0 10px rgba(13, 71, 161, 0.1);

          &::before {
            transform: translateX(0);
          }
        }

        &:active {
          background-color: rgba(13, 71, 161, 0.2);
          transform: scale(0.98);
        }

        &.active {
          background-color: rgba(13, 71, 161, 0.1);
          color: #0d47a1;
          font-weight: 700;
          padding-left: 1.75rem;

          &::before {
            transform: translateX(0);
          }
        }
      }
    }
  }
}

.close-icon {
  font-size: 28px;
  font-weight: bold;
  color: #1d1729;
  transform: rotate(0deg);
  transition: transform 0.4s ease, color 0.3s ease, scale 0.3s ease;
  display: inline-block;

  &:hover {
    transform: rotate(180deg) scale(1.1);
  }
}

@import url("https://fonts.googleapis.com/css2?family=Catamaran:wght@300;400;500;600;700;800;900&family=DM+Sans&display=swap");
// a[href^="tel:"] {
//   text-decoration: none !important;

//   &:hover {
//     text-decoration: none !important;
//   }
// }
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1040;
  visibility: hidden;
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: blur(0px);

  &.show {
    opacity: 1;
    visibility: visible;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(8px);
  }
}

.navbar {
  background: #ffffff !important;
  height: 75px;
  position: relative;
  z-index: 1050;
  padding: 4rem 0 !important;
  flex-wrap: no-wrap !important;

  @media (max-width: 991.99px) {
    height: auto;
    padding: 0 !important;
  }

  .navbar-brand {
    width: 10rem;
    height: auto;
    padding: 0;
    transition: transform 0.3s ease;
    cursor: pointer;
    &:hover {
      transform: scale(1.05);
    }

    @media (max-width: 768px) {
      width: 150px;
    }

    @media (max-width: 576px) {
      width: 120px;
    }
  }

  .navbar-nav {
    .nav-link {
      color: #1d1729 !important;
      font-weight: 600;
      font-size: 16px;
      padding: 0.5rem 1rem !important;
      margin: 0 0.25rem;
      border-radius: 5px;
      position: relative;

      &:hover {
        color: #0d47a1 !important;
      }
    }

    .nav-item.active .nav-link {
      color: #0d47a1 !important;
      font-weight: 700;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 20px;
        height: 3px;
        background-color: #0d47a1;
        border-radius: 2px;
      }
    }

    .phone-number .phone-text {
      color: #0d47a1 !important;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 8px;

      .phone-icon {
        color: #0d47a1;
      }

      &:hover {
        color: #0d47a1 !important;
      }
    }

    .quote-btn {
      background-color: #0d47a1 !important;
      border-color: #0d47a1 !important;
      color: white !important;
      font-weight: 700;
      font-size: 14px;
      padding: 0.5rem 1.2rem !important;
      border-radius: 5px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-left: 1.5rem;

      &:hover {
        background-color: #0a1f5b !important;
        border-color: #0a1f5b !important;
        color: white !important;
      }
    }
  }

  @media (min-width: 992px) {
    .navbar-nav {
      .nav-link {
        transition: none !important;
        &:hover {
          transform: none !important;
        }
      }

      .phone-number .phone-text {
        .phone-icon {
          transition: none !important;
          &:hover {
            transform: none !important;
          }
        }
      }

      .quote-btn {
        transition: none !important;
        &:hover {
          transform: none !important;
          box-shadow: none !important;
        }
      }
    }
  }

  .navbar-toggler {
    border: none;
    padding: 0.25rem 0.5rem;
    z-index: 1051;
    position: relative;
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 8px;
    margin-right: 1.5rem;
    background: transparent !important;

    &:focus {
      box-shadow: none;
    }

    .navbar-toggler-icon {
      display: none;
    }

    .hamburger-lines {
      width: 24px;
      height: 18px;
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .line {
      display: block;
      height: 2px;
      width: 100%;
      border-radius: 2px;
      background: #1d1729;
      transition: all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      transform-origin: center;
    }

    &.active {
      .line1 {
        transform: rotate(45deg) translate(5px, 5px);
      }

      .line2 {
        opacity: 0;
        transform: scaleX(0);
      }

      .line3 {
        transform: rotate(-45deg) translate(7px, -6px);
      }
    }

    &:hover:not(.active) {
      .line {
        background: #0d47a1;
      }
    }
  }

  @media (max-width: 991.99px) {
    .navbar-collapse {
      position: absolute !important;
      top: 100% !important;
      left: 0 !important;
      right: 0 !important;
      width: 100vw !important;
      margin-left: calc(-50vw + 50%) !important;
      background: #ffffff !important;
      padding: 0 !important;
      z-index: 1041 !important;
      opacity: 0 !important;
      visibility: hidden !important;
      transform: translateY(-100%) !important;
      transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1) !important;
      transform-origin: top center !important;
      border-radius: 0 0 20px 20px;
      overflow: hidden;
      max-height: 0 !important;

      &.show {
        opacity: 1 !important;
        visibility: visible !important;
        transform: translateY(0) !important;
        max-height: 600px !important;

        .nav-item {
          animation: fadeInDown 0.8s ease forwards;

          &:nth-child(1) {
            animation-delay: 0.05s;
          }
          &:nth-child(2) {
            animation-delay: 0.1s;
          }
          &:nth-child(3) {
            animation-delay: 0.15s;
          }
          &:nth-child(4) {
            animation-delay: 0.2s;
          }
          &:nth-child(5) {
            animation-delay: 0.25s;
          }
          &:nth-child(6) {
            animation-delay: 0.3s;
          }
        }
      }

      &.closing {
        opacity: 0 !important;
        visibility: visible !important;
        transform: translateY(-100%) !important;
        max-height: 0 !important;
        transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1) !important;

        .nav-item {
          animation: none !important;
          opacity: 1 !important;
        }
      }

      .dropdown-menu {
        position: static;
        border: none;
        box-shadow: none;
        padding: 0 0 0 1.5rem;

        .dropdown-item {
          padding: 0.5rem 1rem;
          font-size: 14px;
          color: #1d1729;

          &:hover {
            color: #0d47a1;
            transform: translateX(10px);
          }
        }
      }

      .dropdown-toggle::after {
        display: none;
      }
    }

    .navbar-nav {
      width: 100% !important;
      padding: 1rem 0 !important;

      .nav-item {
        width: 100% !important;
        margin: 0 !important;
        opacity: 0;
        transform: translateY(-10px);

        &.active {
          background-color: rgba(13, 71, 161, 0.1) !important;
          border-left: 4px solid #0d47a1 !important;
          color: #0d47a1 !important;

          .nav-link {
            color: #0d47a1 !important;
            font-weight: 700 !important;

            &::before {
              width: 100% !important;
            }

            &::after {
              display: none !important;
            }
          }
        }
        &:hover {
          background-color: rgba(13, 71, 161, 0.1) !important;
          border-left: 4px solid #0d47a1 !important;
          color: #0d47a1 !important;
          .nav-link {
            color: #0d47a1 !important;
            font-weight: 700 !important;

            &::before {
              width: 100% !important;
            }

            &::after {
              display: none !important;
            }
          }
        }

        .nav-link {
          text-align: left !important;
          padding: 1.2rem 2rem !important;
          margin: 0 !important;
          border-radius: 0 !important;
          display: block !important;
          border-left: 4px solid transparent;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          position: relative;
          overflow: hidden;

          &::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 0;
            width: 0;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(13, 71, 161, 0.1),
              transparent
            );
            transform: translateY(-50%);
            transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }

          // &:hover {
          //   background-color: rgba(13, 71, 161, 0.05) !important;
          //   transform: translateX(15px) !important;
          //   border-left-color: #0d47a1 !important;
          //   color: #0d47a1 !important;

          //   &::before {
          //     width: 100%;
          //   }
          // }
        }
      }
    }
  }

  @media (max-width: 575.99px) {
    .navbar-collapse {
      top: 65px;
      height: calc(100vh - 65px);
      padding: 1.5rem 0.8rem;
    }

    .navbar-nav .nav-item .nav-link {
      font-size: 16px !important;
    }

    .navbar-nav .quote-btn {
      font-size: 12px !important;
      padding: 0.6rem 1rem !important;
    }

    .navbar-nav .phone-number .phone-text {
      font-size: 14px !important;
    }
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOutUp {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20%);
  }
}

@keyframes fadeOutUpItem {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
}

body.mobile-menu-open {
  overflow: hidden;
}

h1,
p,
span {
  font-family: "Montserrat", sans-serif;
}

#app {
  display: flex !important;
  flex-direction: column !important;
  min-height: 100vh !important;
  background: #f8fbff !important;
}

.main {
  flex-grow: 1 !important;
}

img {
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
}

* {
  scroll-behavior: smooth !important;
}

.cursor--pointer {
  cursor: pointer;
}

.primaryblue--btn {
  background: #0d47a1 !important;
  border-color: #0d47a1 !important;
  text-transform: uppercase;
  &:hover {
    background: #0d47a1 !important;
  }
}

.primary--text {
  color: #0d47a1 !important;
}

.btn:focus {
  box-shadow: none !important;
}

.footer {
  &-text {
    font-size: 16px;
    line-height: 30px;
    color: #1d1729;
    font-weight: 400;
    cursor: pointer;
  }

  @media (max-width: 450px) {
    flex-direction: column !important;
    p {
      margin-left: 0px !important;
    }
  }
}
.contact {
  margin-right: 5rem;
  @media (max-screen: 992px) {
    margin-right: 0;
  }
}
/* Floating Emergency Button Styles */
.emergency-btn {
  position: fixed;
  bottom: 40px;
  right: 10px;
  background-color: #0d47a1;
  color: white;
  padding: 10px 15px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transition: all 0.3s ease;
  text-decoration: none;
  overflow: hidden;

  span {
    // opacity: 0;
    display: none;
    transform: translateX(10px);
    transition: opacity 0.3s ease, transform 0.3s ease;
    white-space: nowrap;
  }

  &:hover {
    background-color: #0a1f5b;
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
    color: white;
    padding-right: 20px;

    span {
      // opacity: 1;
      display: inline;
      transform: translateX(0);
    }
  }

  .emergency-icon {
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }

  &:hover .emergency-icon {
    transform: rotate(360deg);
  }

  @media (max-width: 576px) {
    font-size: 14px;
    padding: 10px 15px;
    .emergency-icon {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
